import React, { useMemo, useState } from 'react';
import { ArrowLeft, AlertTriangle, CheckCircle2, Info, Loader2, ShieldAlert, Edit3 } from 'lucide-react';
import { ItemsDto } from '../../../../types/items';
import {
  ItemTypeChangePreview,
  ItemTypeChangeStrategy,
  ItemTypeChangeManualEntry,
  ItemTypeChangeRequest,
  ItemTypeChangeResult,
  ItemTypeTransitionClass,
  getItemTypeLabel,
} from '../../../../types/itemTypeChange';
import { itemTypeChangeService } from '../../../../services/itemTypeChangeService';
import { ManualRegistrationTable } from './ManualRegistrationTable';

interface Step3Props {
  item: ItemsDto;
  preview: ItemTypeChangePreview;
  strategy: ItemTypeChangeStrategy;
  onStrategyChange: (s: ItemTypeChangeStrategy) => void;
  manualEntries: ItemTypeChangeManualEntry[];
  onManualEntriesChange: (entries: ItemTypeChangeManualEntry[]) => void;
  reason: string;
  onReasonChange: (s: string) => void;
  onBack: () => void;
  onDone: () => void;
}

const strategyLabel: Record<ItemTypeChangeStrategy, string> = {
  [ItemTypeChangeStrategy.Direct]: 'Direkte (ingen datamigrering)',
  [ItemTypeChangeStrategy.Aggregate]: 'Aggreger rader per lokasjon',
  [ItemTypeChangeStrategy.Placeholder]: 'Generer placeholder-verdier',
  [ItemTypeChangeStrategy.Manual]: 'Manuell registrering rad-for-rad',
};

const transitionLabel: Record<ItemTypeTransitionClass, string> = {
  [ItemTypeTransitionClass.Trivial]: 'Triviell — ingen datamigrering',
  [ItemTypeTransitionClass.Downgrade]: 'Nedgradering — aggregering kreves',
  [ItemTypeTransitionClass.UpgradeWithStock]: 'Oppgradering — nye data kreves',
};

export const Step3PreviewConfirm: React.FC<Step3Props> = ({
  item,
  preview,
  strategy,
  onStrategyChange,
  manualEntries,
  onManualEntriesChange,
  reason,
  onReasonChange,
  onBack,
  onDone,
}) => {
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<ItemTypeChangeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const hasBlockers = preview.blockers.length > 0;

  // Valideringsstatus for manuell strategi
  const manualValidation = useMemo(() => {
    if (strategy !== ItemTypeChangeStrategy.Manual) return { valid: true, errors: [] as string[] };
    const errors: string[] = [];
    if (preview.binRows.length !== manualEntries.length) {
      errors.push('Alle lokasjoner må ha entries.');
    }
    for (const row of preview.binRows) {
      const entry = manualEntries.find((e) => e.itemBinsId === row.itemBinsId);
      if (!entry) {
        errors.push(`Lokasjon ${row.binCode}: mangler registrering.`);
        continue;
      }
      if (preview.newItemType === 6) {
        const count = entry.serialNumbers?.filter((s) => s.trim().length > 0).length ?? 0;
        if (Math.abs(count - row.quantity) > 0.001) {
          errors.push(
            `Lokasjon ${row.binCode}: ${count} serienumre angitt, ${row.quantity} kreves.`
          );
        }
        const nonEmpty = entry.serialNumbers?.filter((s) => s.trim().length > 0) ?? [];
        if (new Set(nonEmpty).size !== nonEmpty.length) {
          errors.push(`Lokasjon ${row.binCode}: duplikate serienumre.`);
        }
      } else if (preview.newItemType === 4 || preview.newItemType === 5) {
        const batches = entry.batches?.filter((b) => b.batchNo.trim().length > 0) ?? [];
        const sum = batches.reduce((s, b) => s + (b.quantity || 0), 0);
        if (Math.abs(sum - row.quantity) > 0.001) {
          errors.push(
            `Lokasjon ${row.binCode}: sum av batch-mengder ${sum}, ${row.quantity} kreves.`
          );
        }
        if (preview.newItemType === 4 && batches.some((b) => !b.expiryDate)) {
          errors.push(`Lokasjon ${row.binCode}: utløpsdato kreves for alle batcher.`);
        }
        const ids = batches.map((b) => b.batchNo);
        if (new Set(ids).size !== ids.length) {
          errors.push(`Lokasjon ${row.binCode}: duplikate batch-nummer.`);
        }
      }
    }
    return { valid: errors.length === 0, errors };
  }, [strategy, manualEntries, preview]);

  const canExecute =
    !hasBlockers &&
    confirmed &&
    !executing &&
    (strategy !== ItemTypeChangeStrategy.Manual || manualValidation.valid);

  const handleExecute = async () => {
    try {
      setExecuting(true);
      setError(null);
      const req: ItemTypeChangeRequest = {
        itemNo: preview.itemNo,
        newItemType: preview.newItemType,
        strategy,
        reason: reason.trim() || undefined,
        manualEntries:
          strategy === ItemTypeChangeStrategy.Manual ? manualEntries : undefined,
      };
      const res = await itemTypeChangeService.execute(req);
      setResult(res);
    } catch (e: any) {
      setError(e?.message || 'Utføring feilet');
    } finally {
      setExecuting(false);
    }
  };

  if (result) {
    const usedPlaceholder = result.strategyUsed === ItemTypeChangeStrategy.Placeholder;
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 p-4 rounded-md">
          <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-green-800">Varetype oppdatert</h3>
            <p className="text-sm text-green-700 mt-1">{result.message}</p>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-green-700">
              <div>
                Ny type:{' '}
                <span className="font-semibold">{getItemTypeLabel(result.newItemType)}</span>
              </div>
              <div>Rader oppdatert: {result.rowsUpdated}</div>
              <div>Rader lagt til: {result.rowsInserted}</div>
              <div>Rader slettet: {result.rowsDeleted}</div>
            </div>
          </div>
        </div>

        {usedPlaceholder && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-1">
              <Info className="h-4 w-4" />
              Auto-genererte verdier kan korrigeres
            </div>
            <p className="text-xs text-amber-800 mb-2">
              Placeholder-verdiene (MIG-* / LEGACY-*) er bare midlertidige. Bruk editoren for å
              registrere ekte serie-/batchnummer når de er kjent.
            </p>
            <a
              href={`/?module=placeholder-editor&itemNo=${encodeURIComponent(result.itemNo)}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 underline hover:text-amber-700"
            >
              <Edit3 className="h-3 w-3" />
              Rediger serie-/batchnummer nå
            </a>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={onDone}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            Ferdig
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Steg 3: Gjennomgang og bekreftelse</h2>
      </div>

      {/* Oppsummering */}
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <span className="font-semibold text-gray-700">Vare:</span>{' '}
            <span className="font-mono">{preview.itemNo}</span> — {preview.description || '-'}
          </div>
          <div>
            <span className="font-semibold text-gray-700">Overgang:</span>{' '}
            {getItemTypeLabel(preview.currentItemType)} →{' '}
            <span className="font-semibold">{getItemTypeLabel(preview.newItemType)}</span>
          </div>
          <div>
            <span className="font-semibold text-gray-700">Klassifisering:</span>{' '}
            {transitionLabel[preview.transitionClass]}
          </div>
          <div>
            <span className="font-semibold text-gray-700">Lokasjoner berørt:</span>{' '}
            {preview.affectedLocations} ({preview.itemBinsCount} rader, {preview.totalQuantity} enheter)
          </div>
          {preview.existingSerialsCount > 0 && (
            <div>
              <span className="font-semibold text-gray-700">Eksisterende serienumre:</span>{' '}
              {preview.existingSerialsCount}
            </div>
          )}
          {preview.existingBatchesCount > 0 && (
            <div>
              <span className="font-semibold text-gray-700">Eksisterende batcher:</span>{' '}
              {preview.existingBatchesCount}
            </div>
          )}
          {preview.openPListLinesCount > 0 && (
            <div>
              <span className="font-semibold text-gray-700">Åpne plukklinjer:</span>{' '}
              {preview.openPListLinesCount}
            </div>
          )}
          {preview.openPOrderLinesCount > 0 && (
            <div>
              <span className="font-semibold text-gray-700">Åpne innkjøpsordrer:</span>{' '}
              {preview.openPOrderLinesCount}
            </div>
          )}
        </div>
      </div>

      {/* Blockers */}
      {preview.blockers.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 space-y-1">
          <div className="flex items-center gap-2 text-red-800 font-semibold text-sm">
            <ShieldAlert className="h-4 w-4" />
            Blokkert
          </div>
          {preview.blockers.map((b, i) => (
            <p key={i} className="text-sm text-red-700">
              {b}
            </p>
          ))}
        </div>
      )}

      {/* Warnings */}
      {preview.warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
            <AlertTriangle className="h-4 w-4" />
            Advarsler
          </div>
          {preview.warnings.map((w, i) => (
            <p key={i} className="text-sm text-amber-800">
              {w}
            </p>
          ))}
        </div>
      )}

      {/* Strategivalg */}
      {!hasBlockers && preview.availableStrategies.length > 1 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Velg migreringsstrategi</h3>
          <div className="space-y-2">
            {preview.availableStrategies.map((s) => (
              <label
                key={s}
                className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer ${
                  strategy === s
                    ? 'bg-red-50 border-red-400 ring-2 ring-red-200'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="strategy"
                  checked={strategy === s}
                  onChange={() => onStrategyChange(s)}
                  className="mt-1"
                />
                <div>
                  <p className="font-medium text-sm text-gray-900">{strategyLabel[s]}</p>
                  {s === ItemTypeChangeStrategy.Placeholder && (
                    <p className="text-xs text-gray-600 mt-0.5">
                      {preview.newItemType === 6
                        ? 'Genererer unikt MIG-* serienummer per enhet. Kan korrigeres via varetelling senere.'
                        : 'Tildeler LEGACY-{yyyyMMdd} som batch for alle lokasjoner.'}
                    </p>
                  )}
                  {s === ItemTypeChangeStrategy.Manual && (
                    <p className="text-xs text-gray-600 mt-0.5">
                      Registrer ekte serie-/batch-verdier per lokasjon i tabellen under.
                    </p>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Manuell registrering */}
      {!hasBlockers && strategy === ItemTypeChangeStrategy.Manual && (
        <ManualRegistrationTable
          binRows={preview.binRows}
          newItemType={preview.newItemType}
          entries={manualEntries}
          onEntriesChange={onManualEntriesChange}
        />
      )}

      {!manualValidation.valid && strategy === ItemTypeChangeStrategy.Manual && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
            <Info className="h-4 w-4" />
            Valideringsfeil
          </div>
          {manualValidation.errors.map((e, i) => (
            <p key={i} className="text-sm text-amber-800">
              • {e}
            </p>
          ))}
        </div>
      )}

      {/* Begrunnelse */}
      {!hasBlockers && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Begrunnelse (loggføres i ApplicationLog)
          </label>
          <textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="F.eks. 'Kunde ber om batch-sporing på denne varen fra Q2 2026'"
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
          />
        </div>
      )}

      {/* Bekreftelse */}
      {!hasBlockers && (
        <div className="bg-white border-2 border-red-300 rounded-md p-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-gray-800">
              Jeg forstår at denne endringen påvirker <span className="font-semibold">{preview.itemBinsCount}</span>{' '}
              ItemBins-rader for vare <span className="font-mono">{preview.itemNo}</span>. Endringen
              loggføres og kan ikke angres automatisk.
            </span>
          </label>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 p-3 rounded-md">
          <ShieldAlert className="h-4 w-4 text-red-600 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Tilbake
        </button>
        <button
          onClick={handleExecute}
          disabled={!canExecute}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {executing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
          Utfør endring
        </button>
      </div>
    </div>
  );
};
